/* eslint max-lines: "off" */
import {
  AbstractType,
  Injectable,
  InjectionToken,
  Injector,
  Type,
} from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  GridApi,
  ICellRendererParams,
  RowNode,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import {
  assign,
  find,
  get,
  includes,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  join,
  merge,
  size,
  some,
} from 'lodash';

import { Observable, rx } from '@shared/rx';
import {
  getAllRows,
  getDisplayedItemsIds,
  setColumnDef,
} from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { alphanumericComparator } from '@app/shared/common/alpha-numeric.comparator/alpha-numeric.comparator';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import {
  CELL_RENDERER,
  ColAdditionParams,
  CreateFormRules,
  ENTITY_TYPES,
  GRID_LIST_VIEW_TYPE,
  GridRowData,
  IApiListResponse,
  IBaseEntityType,
  IBaseFormService,
  IFilterQuery,
  IOrderFormFieldPath,
  ISite,
  IXVIListColumn,
  SORT_ORDERS,
  SiteType,
  XuxGridAction,
  XuxGridActionPayload,
  IPageJumpRouteState,
  EntityId,
  MEASURE_TRANSACTION,
  IMeasure,
  ICommonFloatInputRenderParamsResult,
} from '@app/shared/types';
import {
  AUTO_POPULATE_KEY,
  flatTreeDataByHasChildren,
  generateSpacesText,
  generateUnitTextV2,
  getEmptyHiddenCellSelector,
  getObservableValue,
  getQuickFilterConfigForScanning,
  toDate,
  toMoment,
} from '@app/shared/utils';
import { getDateWithDaysDiffTime } from '@app/shared/utils/date-utils';
import { UI_DIMENSIONS } from '@config/ui-dimensions';

import { DataSource, EntityServiceProvider } from '@shared/services/datasource';
import {
  getFormFieldValue,
  setFormFieldValue,
} from '@components/form/services/schema-form/schema-form-functions';
import { BaseEditorContainerComponent } from '@app/containers/base';
import { ORDER_STATUS_VALUE } from '@app/shared/types/enums/order-status.enum';
import { NumberFormatService } from '@shared/services/number-format';
import { CountryFormatService } from '@shared/services/country-format';
import { generateDecimalRegexp } from '@app/shared/constants';
import { OrdersEditContainerComponent } from '@containers/orders/containers';
import { lazyInject } from '@containers/base/decorators';
import { COMMON_WARN_ICON_COLUMN } from '@shared/constants/common-warn-grid-setting';
import { ORDER_GRID_COL_TITLES } from '@app/containers/orders/containers/edit/order-form-col-title-contansts';
import { COLUMN_ALIGN_RIGHT } from '@shared/constants/column-align-definition';
import { BaseFormService } from '@shared/services/base';
import { createFormRules as createOrdersFormRules } from './form-rules';
import { ComponentSelectorResult } from 'ag-grid-community/dist/lib/components/framework/userComponentFactory';
import { ListColumn } from '@shared/types';
import { MAX_PAGE_SIZE } from '@config/global-constants';
import {
  DecimalNumber,
  stringCaseInsensitiveComparator,
  getGroupStrCaseInsensitiveComparator,
} from '@app/shared/common';
import {
  unitCountLimit,
  hiddenVendorStatus,
  getCommonFloatInputRenderParams,
} from './orders.definition';
import { StateService, STATE_KEYS } from '../state-service';
import {
  handleOrderUnitChange,
  updateInitialFactorValueAndCount,
} from './order.util';
import { setPrimaryAndWarehouseTopAndAddStoreNumber } from '@shared/services/transfer/transfer.util';
import { select } from '@angular-redux/store';
import { ORDER_FORM_FIELD_PATHS } from './order-form-field-paths';
import { ScanningService } from '@shared/services/scanning';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class OrderItemFormService
  extends BaseFormService
  implements IBaseFormService<ENTITY_TYPES.ORDER>
{
  public static ENTITY_TYPE: ENTITY_TYPES = ENTITY_TYPES.ORDER;

  public get gridListView(): GRID_LIST_VIEW_TYPE {
    return this.stateService.getOrderItemListViewSetting();
  }

  @lazyInject(EntityServiceProvider)
  public entityServiceProvider: EntityServiceProvider;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(CountryFormatService)
  public countryFormatService: CountryFormatService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(ScanningService) public scanningService: ScanningService;

  @select(STATE_KEYS.UI_EDITOR_OPTIONS_STATE)
  public routeState$: Observable<IPageJumpRouteState>;

  public createFormRulesFunction: CreateFormRules<OrderItemFormService> =
    createOrdersFormRules;
  public listGridApi: GridApi;
  public form: FormGroup;
  public xuxGrid: XuxEditGridComponent;
  public minimumDate: string;
  public dateLockedErrorCode: string = 'date_locked';
  /**
   * cost step
   */
  public step: number;
  public siteIdDataSource: DataSource;
  public vendorDataSource: DataSource;

  public constructor(public injector: Injector) {
    super(injector);
    this.step = new DecimalNumber(10)
      .pow(-this.countryFormatService.getCurrencyPrecision())
      .valueOf();
    this.initVendorDataSource();
    this.initSiteIdDataSource();
  }

  public initKeyDownEventSubscription(): void {
    this.scanningService.scanOptions = {
      isCategoryListView: this.isCategoryListView(),
      focusKeyForListModal: 'count',
      focusKeyForTreeModal: 'count',
      isCompleted: this.isFinalStatus(this.form),
      focusOnNextRow: false,
      customEntityIdPath: 'item_entity_id',
      customNamePath: 'item_name',
    };
    const scanningSubscription =
      this.scanningService.returnScanningSubscription(
        this.scanningService.scanOptions,
      );
    this.subscriptions.push(scanningSubscription);
  }

  public onFormReady(form: FormGroup): void {
    this.form = form;

    this.autoPopulate();

    this.vendorDataSource?.refresh();
    this.siteIdDataSource?.refresh();
    this.initKeyDownEventSubscription();
  }

  public destroyFormDefinition(): void {
    this.scanningService.destroyServiceDefinition();
    super.destroyFormDefinition();
  }

  /**
   * auto populate some data
   * current will populate event and from site
   *
   * @returns promise void
   */
  public async autoPopulate(): Promise<void> {
    // only do auto populate when is create
    if (!this.isNewMode()) {
      return;
    }

    // update pageJumpParameters for handle historical entry
    const routeState = getObservableValue(this.routeState$);

    const autoPopulateData = get(routeState, AUTO_POPULATE_KEY);

    if (isEmpty(autoPopulateData)) {
      return;
    }

    await Promise.all([
      this.populateVendor(
        get(autoPopulateData, ORDER_FORM_FIELD_PATHS.vendorEntityId),
      ),
      this.populateWarehouse(
        get(autoPopulateData, ORDER_FORM_FIELD_PATHS.siteId),
      ),
    ]);

    this.populateDate(autoPopulateData, ORDER_FORM_FIELD_PATHS.orderDate);

    this.populateDate(autoPopulateData, ORDER_FORM_FIELD_PATHS.deliveryDate);
  }

  public populateDate(baseOnData: IBaseEntityType, fieldPath: string): void {
    const newDate = get(baseOnData, fieldPath);

    if (toMoment(this.minimumDate).isSameOrBefore(toMoment(newDate))) {
      setFormFieldValue(this.form, fieldPath, newDate);
    }
  }

  public async populateVendor(vendorEntityId: EntityId): Promise<void> {
    if (isEmpty(vendorEntityId)) {
      return;
    }

    const vendors = await this.vendorDataSource.nextPage();

    const autoPopulateVendor = find(vendors, { entity_id: vendorEntityId });

    if (isEmpty(autoPopulateVendor)) {
      return;
    }

    setFormFieldValue(
      this.form,
      ORDER_FORM_FIELD_PATHS.vendorEntityId,
      vendorEntityId,
    );
  }

  public async populateWarehouse(warehouseEntityId: EntityId): Promise<void> {
    if (isEmpty(warehouseEntityId)) {
      return;
    }

    const sites = await this.siteIdDataSource.nextPage();

    const autoPopulateWarehouse = find(sites, { id: warehouseEntityId });

    if (isEmpty(autoPopulateWarehouse)) {
      return;
    }

    setFormFieldValue(
      this.form,
      ORDER_FORM_FIELD_PATHS.siteId,
      warehouseEntityId,
    );
  }

  public initVendorDataSource(): void {
    this.vendorDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.VENDOR,
    );
    this.vendorDataSource.fetchList = this.vendorDataSourceFetchList.bind(this);
  }

  public initSiteIdDataSource(): void {
    this.siteIdDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.SITE,
    );
    if (this.siteIdDataSource.service) {
      this.siteIdDataSource.service.quickFilterFields = [
        'name',
        'store_number',
      ];
    }
    this.siteIdDataSource.fetchList = this.siteIdDataSourceFetchList.bind(this);
  }

  public vendorDataSourceFetchList(query): Promise<IApiListResponse> {
    const status: ORDER_STATUS_VALUE = getFormFieldValue(this.form, 'status');

    if (includes(hiddenVendorStatus, status)) {
      return this.preloadVendorDatasourceItems();
    }

    const queryWithDefaults: IFilterQuery = merge(
      {
        filter: `is_active eq true`,
        orderby: 'name asc',
      },
      {
        ...query,
        top: MAX_PAGE_SIZE,
      },
    );

    return this.vendorDataSource.service
      .fetchList(queryWithDefaults)
      .pipe(rx.first())
      .toPromise()
      .then(
        (result) => result,
        (error) => (
          this.vendorDataSource.error.next(error), Promise.reject(error)
        ),
      );
  }

  public siteIdDataSourceFetchList(query): Promise<IApiListResponse> {
    const status: ORDER_STATUS_VALUE = getFormFieldValue(this.form, 'status');
    const filterTypesStr = join(
      [SiteType.Warehouse, SiteType.PrimaryWarehouse],
      ',',
    );

    if (includes(hiddenVendorStatus, status)) {
      return this.preloadSiteIdDatasourceItems();
    }

    const queryWithDefaults: IFilterQuery = merge(
      {
        filter: `(type eq in(${filterTypesStr}) or business_type eq in(${filterTypesStr})) and is_active eq true`,
        select: ['name', 'id', 'type', 'business_type', 'store_number'],
        filter_by_assigned_sites: true,
        include_inactive: false,
      },
      {
        ...query,
        top: MAX_PAGE_SIZE,
      },
    );

    return this.siteIdDataSource.service
      .fetchList(queryWithDefaults)
      .pipe(
        rx.first(),
        rx.map((response) => {
          response.items = setPrimaryAndWarehouseTopAndAddStoreNumber(
            response.items as ISite[],
          );
          response.total = size(response.items);

          return response;
        }),
      )
      .toPromise()
      .then(
        (result) => result,
        (error) => (
          this.siteIdDataSource.error.next(error), Promise.reject(error)
        ),
      );
  }

  public preloadVendorDatasourceItems(): Promise<IApiListResponse> {
    const vendorName = getFormFieldValue(this.form, 'vendor_name');
    const vendorEntityId = getFormFieldValue(this.form, 'vendor_entity_id');
    const listResponse = {
      items: [
        {
          name: vendorName,
          entity_id: vendorEntityId,
        },
      ],
    } as IApiListResponse<any>;

    return Promise.resolve(listResponse);
  }

  public preloadSiteIdDatasourceItems(): Promise<IApiListResponse> {
    const storeNumber = getFormFieldValue(this.form, 'store_number');
    const name = getFormFieldValue(this.form, 'site_name');
    const siteId = getFormFieldValue(this.form, 'site_id');
    const listResponse = {
      items: [
        {
          name: generateSpacesText(name, storeNumber),
          id: siteId,
        },
      ],
    } as IApiListResponse<any>;

    return Promise.resolve(listResponse);
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  /**
   * check is Open Status
   *
   * @param form
   * @returns boolean
   */
  public isOpenStatus(form: FormGroup): boolean {
    if (!form) return false;

    return includes(
      [ORDER_STATUS_VALUE.New, ORDER_STATUS_VALUE.Open],
      getFormFieldValue(form, 'status'),
    );
  }

  /**
   * check is final status
   *
   * @param form
   * @returns boolean
   */
  public isFinalStatus(form: FormGroup): boolean {
    if (!form) return false;

    return includes(
      [
        ORDER_STATUS_VALUE.Canceled,
        ORDER_STATUS_VALUE.Completed,
        ORDER_STATUS_VALUE.Rejected,
      ],
      getFormFieldValue(form, 'status'),
    );
  }

  public getInlineChangeMapper(
    editor: OrdersEditContainerComponent,
  ): (value: GridRowData, payload: XuxGridActionPayload) => GridRowData {
    return (value: GridRowData, payload: XuxGridActionPayload): GridRowData => {
      let newGridRowData: GridRowData = value;
      if (this.isOpenStatus(get(editor, 'form'))) {
        const vendorEntityIdValue: string = editor.getFormFieldValue(
          editor.formFieldPath.vendorEntityId,
        );
        switch (payload.field) {
          case editor.formFieldPath.measureEntityId:
            const updatedRowData: GridRowData = handleOrderUnitChange(
              value,
              vendorEntityIdValue,
            );

            newGridRowData = updatedRowData;
            break;
          case editor.formFieldPath.itemCount:
            const initialFactorValueAndCount =
              updateInitialFactorValueAndCount(value);

            newGridRowData = {
              ...value,
              initialFactorValueAndCount,
            };
            break;
          default:
            newGridRowData = value;
            break;
        }
      }

      return merge({}, newGridRowData, {
        [editor.formFieldPath.initialItemCount]: this.isOpenStatus(
          get(editor, 'form'),
        )
          ? newGridRowData[editor.formFieldPath.itemCount]
          : get(value, editor.formFieldPath.initialItemCount),
        [editor.formFieldPath.initialItemCost]: this.isOpenStatus(
          get(editor, 'form'),
        )
          ? newGridRowData[editor.formFieldPath.itemCost]
          : get(value, editor.formFieldPath.initialItemCost),
      });
    };
  }

  /**
   * number formatter
   *
   * @param params ValueFormatterParams of row.
   * @returns formatted param value
   */
  public orderUnitFormatter(params: ValueFormatterParams | RowNode): string {
    if (
      isEmpty(params.data?.measure_description) ||
      !this.numberFormatService
    ) {
      return '';
    }

    return generateUnitTextV2(
      {
        description: params.data.measure_description,
        factor_value: params.data.measure_factor_value,
      } as IMeasure,
      {
        numberFormatService: this.numberFormatService,
        measureTransactionType: MEASURE_TRANSACTION.Order,
        notNeedAddSign: true,
      },
    );
  }

  /**
   * get alphanumeric with other field comparator
   * will compare value first, then will compare other field when value is equal
   *
   * @param otherField target other field, if null, will not compare when value is equal
   * @returns comparator
   */
  public getAlphanumericWithOtherFieldComparator(
    otherField: string = '',
    parseNaNToNull: boolean = false,
  ): (valueA: any, valueB: any, nodeA: RowNode, nodeB: RowNode) => number {
    return (
      valueA: any,
      valueB: any,
      nodeA: RowNode,
      nodeB: RowNode,
    ): number => {
      let compareValueA = valueA;
      let compareValueB = valueB;

      if (!!parseNaNToNull) {
        compareValueA = isNaN(valueA) ? null : valueA;
        compareValueB = isNaN(valueB) ? null : valueB;
      }

      const curCompareResult = alphanumericComparator(
        compareValueA,
        compareValueB,
      );

      if (curCompareResult === 0 && !isEmpty(otherField)) {
        return alphanumericComparator(
          nodeA.data?.[otherField],
          nodeB.data?.[otherField],
        );
      }

      return curCompareResult;
    };
  }

  /**
   * will fire this when list grid ready,
   *
   * @param editor OrdersEditContainerComponent
   */
  public getGridReadyHandler(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    editor: OrdersEditContainerComponent,
  ): (action: XuxGridAction, grid: XuxEditGridComponent) => void {
    return (action: XuxGridAction, grid: XuxEditGridComponent): void => {
      this.xuxGrid = grid;
      this.listGridApi = action.payload?.gridApi;
      if (this.isOpenStatus(get(editor, 'form'))) {
        setColumnDef(this.listGridApi, editor.formFieldPath.measureEntityId, {
          headerComponentParams: {
            hintOptions: {
              description: this.i18nService.instant(
                I18n.InfoMessages.DefaultOrderingUnitHint,
              ),
              placement: 'top',
            },
          },
        });
      }
      this.listGridApi.expandAll();
      this.scanningService.gridApi = this.listGridApi;
      this.scanningService.columnApi = get(
        action,
        'payload.gridApi.columnController.columnApi',
      );
    };
  }

  /**
   * get no rows overlay message
   * if filter empty will use filterEmptyDataTips, otherwise will use emptyDataTips
   *
   * @returns no rows overlay message
   */
  public listGridNoRowsOverlayMessageFunc(): string {
    const emptyDataTips = I18n.InfoMessages.YouHaveNotAddedAnyItemsYet;
    const filterEmptyDataTips = I18n.Common.NoRowsToShow;

    if (!this.listGridApi) {
      return emptyDataTips;
    }

    const allGridRows = getAllRows(this.listGridApi);
    const displayedItemsIds = getDisplayedItemsIds(this.listGridApi);

    return allGridRows.length > 0 && displayedItemsIds.length <= 0
      ? filterEmptyDataTips
      : emptyDataTips;
  }

  /**
   * get default order item list column
   *
   * @param formFieldPath form field path mapping
   * @param status status of order
   * @returns order item list column
   */
  public getDefaultOrderItemListColumn(
    formFieldPath: IOrderFormFieldPath,
    status: ORDER_STATUS_VALUE,
    editor: OrdersEditContainerComponent,
  ): IXVIListColumn[] {
    const isOpenStatus = [
      ORDER_STATUS_VALUE.New,
      ORDER_STATUS_VALUE.Open,
    ].includes(status);
    const isCanceledStatus = [ORDER_STATUS_VALUE.Canceled].includes(status);
    const isPendingStatus =
      !isOpenStatus && status === ORDER_STATUS_VALUE.Pending;
    const isShowReceivedExtendedCost =
      isEqual(ORDER_STATUS_VALUE.Canceled, status) || isOpenStatus;
    const precision = this.stateService.getNumberPrecision();
    const costPrecision = this.countryFormatService.getCurrencyPrecision();

    const floatInputRenderParams = assign(
      { step: this.step, artificialInputCheck: true },
      getCommonFloatInputRenderParams({
        precision,
        acceptableInputRegex: generateDecimalRegexp(precision, false),
      }),
    );
    const initialTotalCostCol: IXVIListColumn = {
      title: ORDER_GRID_COL_TITLES.initialTotalCost,
      path: formFieldPath.initialTotalCost,
      defaultView: true,
      editable: false,
      cellClassRules: {
        'ag-numeric-cell': (): boolean => true,
        'show-placeholder': ({ value }): boolean => !(value || value === 0),
      },
      valueFormatter: (params: ValueFormatterParams): string =>
        params.data.isLeaf
          ? this.numberFormatService.getNumberFormatter(true)(params)
          : null,
      valueGetter: (params: ValueGetterParams): DecimalNumber => {
        if (!params.data.isLeaf) return null;
        const { initial_count, initial_item_cost } = params.data;

        if (this.isOpenStatus(get(editor, 'form'))) {
          return new DecimalNumber(initial_count).mul(initial_item_cost);
        }

        return new DecimalNumber(params.data.initial_total_cost);
      },
      cellRendererSelector: getEmptyHiddenCellSelector(
        formFieldPath.itemEntityId,
      ),
      comparator: this.getAlphanumericWithOtherFieldComparator(
        formFieldPath.itemName,
        true,
      ).bind(this),
      minWidth: 120,
      ...COLUMN_ALIGN_RIGHT,
    };
    const totalCostCol: IXVIListColumn = {
      ...initialTotalCostCol,
      title: !isShowReceivedExtendedCost
        ? ORDER_GRID_COL_TITLES.receivedTotalCost
        : initialTotalCostCol.title,
      path: formFieldPath.totalCost,
      valueGetter: (params: ValueGetterParams): DecimalNumber => {
        if (!params.data.isLeaf) return null;
        const { count, item_cost } = params.data;

        return new DecimalNumber(count).mul(item_cost);
      },
    };
    const receivedCols: IXVIListColumn[] = [
      {
        title: ORDER_GRID_COL_TITLES.quantityReceived,
        defaultView: true,
        path: formFieldPath.itemCount,
        editable: true,
        minWidth: 120,
        valueFormatter: this.numberFormatService.getNumberFormatter(false),
        ...Object.assign(
          {},
          isPendingStatus
            ? {
                cellRendererSelector: getEmptyHiddenCellSelector(
                  formFieldPath.itemEntityId,
                  editor.canEdit ? CELL_RENDERER.float_input_renderer : null,
                  true,
                ),
                cellRendererParams: floatInputRenderParams,
              }
            : {
                cellRendererSelector: getEmptyHiddenCellSelector(
                  formFieldPath.itemEntityId,
                ),
              },
        ),
        ...COLUMN_ALIGN_RIGHT,
      },
      {
        title: ORDER_GRID_COL_TITLES.receivedUnitCost,
        defaultView: true,
        path: formFieldPath.itemCost,
        editable: true,
        minWidth: 120,
        valueFormatter: this.numberFormatService.getNumberFormatter(true),
        ...Object.assign(
          {},
          isPendingStatus
            ? {
                cellRendererSelector: getEmptyHiddenCellSelector(
                  formFieldPath.itemEntityId,
                  editor.canEdit ? CELL_RENDERER.price_input_renderer : null,
                  true,
                ),
                cellRendererParams: {
                  ...floatInputRenderParams,
                  acceptableInputRegex: generateDecimalRegexp(
                    costPrecision,
                    true,
                  ),
                  min: unitCountLimit.minimum,
                  max: unitCountLimit.maximum,
                  usePreferenceDecimalPrecision: {
                    overrideMax: true,
                    overrideMin: false,
                  },
                },
              }
            : {
                cellRendererSelector: getEmptyHiddenCellSelector(
                  formFieldPath.itemEntityId,
                ),
              },
        ),
        ...COLUMN_ALIGN_RIGHT,
      },
    ];
    const colAdditionParams = this.getOpenStatusColAdditionParams(
      formFieldPath,
      floatInputRenderParams,
      editor,
    );
    const statusSharedCols: IXVIListColumn[] = [
      {
        path: 'reporting_category_major.name',
        filterParams: {
          quickFilter: this.isCategoryListView(),
        },
      },
      {
        path: 'reporting_category_minor.name',
        filterParams: {
          quickFilter: this.isCategoryListView(),
        },
      },
      {
        title: I18n.Forms.GridForm.ItemName,
        cellRenderer: CELL_RENDERER.ag_group_cell_renderer,
        cellRendererParams: {
          // turn off the row count
          suppressCount: true,
        },
        sortable: true,
        sort: SORT_ORDERS.ASC,
        sortingOrder: [SORT_ORDERS.ASC, SORT_ORDERS.DESC],
        showRowGroup: true,
        defaultView: true,
        filterParams: {
          quickFilter: true,
        },
        path: formFieldPath.itemName,
        minWidth: 220,
        comparator: this.isCategoryListView()
          ? getGroupStrCaseInsensitiveComparator()
          : stringCaseInsensitiveComparator,
        ...getQuickFilterConfigForScanning(
          this.isCategoryListView(),
          formFieldPath.itemName,
        ),
      },
      {
        title: ORDER_GRID_COL_TITLES.glAccountName,
        path: formFieldPath.glAccountName,
        filterParams: {
          quickFilter: true,
        },
        defaultView: true,
        minWidth: 120,
        ...Object.assign<{}, Partial<IXVIListColumn>>(
          {},
          isOpenStatus ? colAdditionParams.glAccountName : null,
        ),
      },
      {
        title: ORDER_GRID_COL_TITLES.measureEntityId,
        path: formFieldPath.measureEntityId,
        defaultView: true,
        valueFormatter: this.orderUnitFormatter.bind(this),
        minWidth: 120,
        editable: isOpenStatus,
        ...Object.assign(
          {},
          isOpenStatus ? colAdditionParams.measureEntityId : null,
        ),
      },
      {
        title: ORDER_GRID_COL_TITLES.initialItemCount,
        path: isOpenStatus
          ? formFieldPath.itemCount
          : formFieldPath.initialItemCount,
        editable: isOpenStatus,
        defaultView: true,
        valueFormatter: this.numberFormatService.getNumberFormatter(false),
        ...Object.assign(
          {
            cellRendererSelector: getEmptyHiddenCellSelector(
              formFieldPath.itemEntityId,
            ),
          },
          isOpenStatus ? colAdditionParams.itemCount : null,
        ),
        minWidth: 100,
        ...COLUMN_ALIGN_RIGHT,
      },
      {
        title: ORDER_GRID_COL_TITLES.initialItemCost,
        path: isOpenStatus
          ? formFieldPath.itemCost
          : formFieldPath.initialItemCost,
        defaultView: true,
        headerComponentParams: {
          hintOptions: {
            description: this.i18nService.instant(
              I18n.InfoMessages.UnitCostIsLastPurchaseCostOfInventoryItem,
            ),
            placement: 'top',
          },
        },
        valueFormatter: this.numberFormatService.getNumberFormatter(true),
        ...Object.assign(
          {
            cellRendererSelector: getEmptyHiddenCellSelector(
              formFieldPath.itemEntityId,
            ),
          },
          isOpenStatus ? colAdditionParams.itemCost : null,
        ),
        minWidth: 100,
        ...COLUMN_ALIGN_RIGHT,
      },
    ];

    const warnIconRenderer = {
      ...COMMON_WARN_ICON_COLUMN,
      cellStyle: null,
      cellRenderer: null,
      cellRendererSelector: (
        param: ICellRendererParams,
      ): ComponentSelectorResult => {
        return {
          component: param.value
            ? CELL_RENDERER.icon_renderer
            : CELL_RENDERER.empty_renderer,
        };
      },
    };

    // Canceled or Open status don't have received cols
    if (isOpenStatus || isCanceledStatus) {
      return [warnIconRenderer, ...statusSharedCols, totalCostCol];
    }

    return [
      warnIconRenderer,
      ...statusSharedCols,
      initialTotalCostCol,
      ...receivedCols,
      totalCostCol,
    ];
  }

  public getOpenStatusColAdditionParams(
    formFieldPath: IOrderFormFieldPath,
    floatInputRenderParams: {
      step: number;
      artificialInputCheck: boolean;
    } & ICommonFloatInputRenderParamsResult,
    editor: OrdersEditContainerComponent,
  ): ColAdditionParams {
    const openStatusGLAccountAdditionParams: Partial<IXVIListColumn> = {
      width: UI_DIMENSIONS.columnWidthText,
      cellRendererSelector: getEmptyHiddenCellSelector(
        formFieldPath.itemEntityId,
      ),
      comparator: this.getAlphanumericWithOtherFieldComparator(
        formFieldPath.itemName,
      ).bind(this),
    };

    const openStatusOrderUnitAdditionParams: Partial<IXVIListColumn> = {
      path: formFieldPath.measureEntityId,
      width: UI_DIMENSIONS.columnWidthText,
      cellRendererSelector: (
        params: ICellRendererParams,
      ): ComponentSelectorResult | null =>
        params.data.isLeaf && this.isOpenStatus(get(editor, 'form'))
          ? { component: CELL_RENDERER.custom_select_renderer }
          : null,
      cellRendererParams: (
        params: ICellRendererParams,
      ): ListColumn['cellRendererParams'] => {
        if (isEmpty(params.data.measures)) {
          return null;
        }

        const isLastRow =
          params.rowIndex === params.api.getDisplayedRowCount() - 1;

        return {
          options: params.data.measures,
          disabled: !editor.canEdit,
          isPositionTop: isLastRow,
          value: params.data[editor.formFieldPath.measureEntityId],
        };
      },
      valueGetter: (params: ValueGetterParams): number => {
        if (!params.data.isLeaf) return null;
        const currentMeasure = find(get(params.data, 'measures', []), {
          entity_id: params.data.measure_entity_id,
        });

        return this.isOpenStatus(get(editor, 'form'))
          ? params.data[editor.formFieldPath.measureEntityId]
          : generateUnitTextV2(currentMeasure, {
              numberFormatService: this.numberFormatService,
              measureTransactionType: MEASURE_TRANSACTION.Order,
              notNeedAddSign: true,
            });
      },
      cellClass:
        'cell-price ag-cell-editable-renderer cell-overflow-visible-without-hover',
      comparator: (_valueA, _valueB, nodeA, nodeB): number =>
        this.getAlphanumericWithOtherFieldComparator(formFieldPath.itemName)(
          this.orderUnitFormatter(nodeA),
          this.orderUnitFormatter(nodeB),
          nodeA,
          nodeB,
        ),
    };

    const openStatusQuantityOrderedAdditionParams: Partial<IXVIListColumn> = {
      width: UI_DIMENSIONS.columnWidthText,
      cellRendererParams: floatInputRenderParams,
      cellRendererSelector: getEmptyHiddenCellSelector(
        formFieldPath.itemEntityId,
        editor.canEdit ? CELL_RENDERER.float_input_renderer : null,
      ),
      comparator: this.getAlphanumericWithOtherFieldComparator(
        formFieldPath.itemName,
        true,
      ).bind(this),
    };

    const openStatusUnitCostAdditionParams: Partial<IXVIListColumn> = {
      width: UI_DIMENSIONS.columnWidthText,
      cellClassRules: {
        'show-placeholder': ({ value }): boolean => !(value || value === 0),
      },
      cellRendererSelector: getEmptyHiddenCellSelector(
        formFieldPath.itemEntityId,
      ),
      comparator: this.getAlphanumericWithOtherFieldComparator(
        formFieldPath.itemName,
        true,
      ).bind(this),
    };

    return {
      glAccountName: openStatusGLAccountAdditionParams,
      measureEntityId: openStatusOrderUnitAdditionParams,
      itemCount: openStatusQuantityOrderedAdditionParams,
      itemCost: openStatusUnitCostAdditionParams,
    };
  }

  public getInventoryItemVenuesRequireValidator(): ValidatorFn {
    return function (control: AbstractControl): ValidationErrors | null {
      const controlRoot = control.root;

      if (isEmpty(controlRoot.value)) return null;
      const status = getFormFieldValue(this.form, this.formFieldPath.status);
      if (includes(this.finalStatus, status)) {
        return null;
      }

      const data = flatTreeDataByHasChildren<IBaseEntityType>(control.value);

      if (isEmpty(data)) {
        return {
          error: this.i18nService.instant(
            I18n.ErrorMessages
              .AtLeastOneQuantityOrderedMustHaveValueOtherThanZero,
          ),
        };
      }

      const isPendingStatus = ORDER_STATUS_VALUE.Pending === status;
      const quantityReceivedHasValue = some(data, (item) => {
        return !!Number(item.count);
      });
      const receivedCostHasValue = some(data, (item) => {
        return !!Number(item.count) && !isNil(item.item_cost);
      });

      if (isPendingStatus) {
        if (!quantityReceivedHasValue) {
          return {
            error: this.i18nService.instant(
              I18n.ErrorMessages.AtLeastOneQuantityReceivedMustHaveValue,
            ),
          };
        }
        if (!receivedCostHasValue) {
          return {
            error: this.i18nService.instant(
              I18n.ErrorMessages.ReceivedUnitCostMustHaveValue,
            ),
          };
        }

        return null;
      }
      const hasQuantityItems = some(data, (item) => {
        const castNumber = new DecimalNumber(item.count);

        return castNumber.gt(0) || castNumber.lt(0);
      });

      return hasQuantityItems
        ? null
        : {
            error: this.i18nService.instant(
              I18n.ErrorMessages
                .AtLeastOneQuantityOrderedMustHaveValueOtherThanZero,
            ),
          };
    };
  }

  /**
   * get delivery date range validator
   *
   * @param editor container editor object, use to get form value
   * @param orderFieldPath order field path, can't lower than this,
   * @param minimum minimum value, can't lower than this, if not has value will use this.minimumDate
   * @returns delivery date range validator
   */
  public getDeliveryDateRangeValidator(
    editor: BaseEditorContainerComponent,
    orderFieldPath: string,
    minimum: string,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      // is not open status, return null error directly.
      if (!this.isOpenStatus(get(editor, 'form'))) {
        return null;
      }

      const root = control.root;
      const value = control.value;
      const orderDateControl = root?.get(orderFieldPath);
      const minDate = toDate(minimum ?? this.minimumDate);
      const orderDateValue = orderDateControl?.value ?? '';
      const currentTime = getDateWithDaysDiffTime(0, true, toDate(value));
      const orderDateTime = getDateWithDaysDiffTime(
        0,
        true,
        toDate(orderDateValue),
      );
      const minDateTime = getDateWithDaysDiffTime(0, true, minDate);

      if (
        !isEmpty(orderDateValue) &&
        !orderDateControl.hasError(this.dateLockedErrorCode)
      ) {
        orderDateControl?.setErrors(null);
      }

      if (
        !isEmpty(orderDateValue) &&
        currentTime < orderDateTime &&
        currentTime > minDateTime
      ) {
        return {
          earlier_than_transaction_date: this.i18nService.instant(
            I18n.ErrorMessages.OneDateCannotBeEarlierThanAnother,
            {
              oneDate: this.i18nService.instant(
                I18n.Forms.OrderForm.deliveryDate,
              ),
              anotherDate: this.i18nService.instant(
                I18n.Forms.OrderForm.orderDate,
              ),
            },
          ),
        };
      }

      if (currentTime < minDateTime) {
        return {
          [this.dateLockedErrorCode]: this.i18nService.instant(
            I18n.ErrorMessages.DateIsLocked,
          ),
        };
      }

      return null;
    };
  }

  /**
   * get order date range validator
   *
   * @param editor container editor object, use to get form value
   * @param deliveryFieldPath delivery date field path, can't large than this
   * @param minimum minimum date, can't lower than this, if not has value will use this.minimumDate
   * @returns order date range validator
   */
  public getOrderDateRangeValidator(
    editor: BaseEditorContainerComponent,
    deliveryFieldPath: string,
    minimum?: string,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors => {
      // is not open status, return null error directly.
      if (!this.isOpenStatus(get(editor, 'form'))) {
        return null;
      }

      const root = control.root;
      const value = control.value;
      const deliveryDateControl = root?.get(deliveryFieldPath);
      const minDate = toDate(minimum ?? this.minimumDate);
      const deliveryDateValue = deliveryDateControl?.value ?? '';
      const currentTime = getDateWithDaysDiffTime(0, true, toDate(value));
      const minDateTime = getDateWithDaysDiffTime(0, true, minDate);

      if (
        !isEmpty(deliveryDateValue) &&
        !deliveryDateControl.hasError(this.dateLockedErrorCode)
      ) {
        deliveryDateControl?.setErrors(null);
      }

      if (
        !isEmpty(deliveryDateValue) &&
        currentTime >
          getDateWithDaysDiffTime(0, true, toDate(deliveryDateValue)) &&
        currentTime > minDateTime
      ) {
        return {
          later_than_delivery_date: this.i18nService.instant(
            I18n.ErrorMessages.OneDateCannotBeLaterThanAnother,
            {
              oneDate: this.i18nService.instant(I18n.Forms.OrderForm.orderDate),
              anotherDate: this.i18nService.instant(
                I18n.Forms.OrderForm.deliveryDate,
              ),
            },
          ),
        };
      }

      if (currentTime < getDateWithDaysDiffTime(0, true, minDate)) {
        return {
          [this.dateLockedErrorCode]: this.i18nService.instant(
            I18n.ErrorMessages.DateIsLocked,
          ),
        };
      }

      return null;
    };
  }
}
// eslint-disable-next-line max-lines
